/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TaskContext } from '../contexts';
import {
  Category,
  Comment,
  Feedback,
  Filter,
  GELocation,
  GetTaskParams,
  Issue,
  ITaskShareObj,
  ITempTaskWithNewStoplight,
  SCLocation,
  TaskPost
} from '../../config/interfaces';

interface ICompleteTaskPayload {
  deepLink?: boolean;
  edit?: boolean;
  taskId: string;
  taskType: string;
}

interface IGetCategoriesByTypePayload {
  categoryFor: string;
  categoryType: string;
  feedbackType?: string;
}

interface IPostCommentPayload {
  task: TaskPost;
  comment: {
    id: string;
    comment: Comment;
  };
}

interface IGetSearchIssuesPayload {
  query: string;
  origin: string;
  page: number;
}

interface IGetTasksByAccountNumPayload {
  accountNumber: string;
  facilityMnemonic: string;
}

interface IGetTasksByEmployeeIdPayload {
  employeeId: string;
  roundingTypes: string[];
}

export interface ISaveTaskModalStatePayload {
  modal?: boolean;
  modalType?: string;
  modalAction?: string;
  redirect: boolean;
}

export interface ITempTaskWithSharedWith {
  taskObject: ITempTaskWithNewStoplight;
  shareObject: ITaskShareObj;
  sharedWithDisplay: string[];
}

/**
 * Redux slice for managing task-related state using Redux Toolkit's `createSlice`.
 *
 * This slice handles actions related to issues, feedback, and tasks, including:
 * - Fetching, creating, updating, and deleting issues, feedback, and tasks
 * - Completing tasks and managing task lifecycle events
 * - Fetching issues and feedback by various filters (unit, employee, facility, etc.)
 * - Managing categories, filters, search, sorting, and pagination for tasks and feedback
 * - Handling ServiceCentral ticket details and location data
 * - Saving and clearing temporary task and modal state
 * - Managing loading and error states for all asynchronous task operations
 *
 * @property {object} initialState - The initial state for the task context.
 * @property {Function} postNewTask - Initiates posting a new task and sets loading state.
 * @property {Function} postNewTaskSuccess - Handles successful posting of a new task.
 * @property {Function} postNewTaskFailure - Handles failed posting of a new task.
 * @property {Function} completeTask - Initiates completing a task and sets loading state.
 * @property {Function} completeTaskSuccess - Handles successful completion of a task.
 * @property {Function} completeTaskFailure - Handles failed completion of a task.
 * @property {Function} deleteTask - Initiates deleting a task and sets loading state.
 * @property {Function} deleteTaskSuccess - Handles successful deletion of a task.
 * @property {Function} deleteTaskFailure - Handles failed deletion of a task.
 * @property {Function} getCategoriesByType - Initiates fetching categories by type.
 * @property {Function} getCategoriesByTypeSuccess - Handles successful fetch of categories.
 * @property {Function} getCategoriesByTypeFailure - Handles failed fetch of categories.
 * @property {Function} getDetailsByTicketNum - Initiates fetching ServiceCentral ticket details.
 * @property {Function} getDetailsByTicketNumSuccess - Handles successful fetch of ticket details.
 * @property {Function} getDetailsByTicketNumFailure - Handles failed fetch of ticket details.
 * @property {Function} clearTicketDetails - Clears the ticket details from the state.
 * @property {Function} getSCLocations - Initiates fetching ServiceCentral locations.
 * @property {Function} getSCLocationsSuccess - Handles successful fetch of ServiceCentral locations.
 * @property {Function} getSCLocationsFailure - Handles failed fetch of ServiceCentral locations.
 * @property {Function} getGELocations - Initiates fetching GE locations.
 * @property {Function} getGELocationsSuccess - Handles successful fetch of GE locations.
 * @property {Function} getGELocationsFailure - Handles failed fetch of GE locations.
 * @property {Function} verifyControlNumber - Initiates verifying a control number.
 * @property {Function} verifyControlNumberSuccess - Handles successful control number verification.
 * @property {Function} verifyControlNumberFailure - Handles failed control number verification.
 * @property {Function} clearControlNumberResponse - Clears the control number response from the state.
 * @property {Function} getTaskDiscussion - Initiates fetching task discussion.
 * @property {Function} postTaskComment - Initiates posting a comment to a task.
 * @property {Function} searchIssues - Initiates searching issues and sets loading state.
 * @property {Function} searchIssuesSuccess - Handles successful search of issues.
 * @property {Function} searchIssuesFailure - Handles failed search of issues.
 * @property {Function} clearSearchIssues - Clears searched issues from the state.
 * @property {Function} getIssuesByUnitIdsOrEmployeeList - Initiates fetching issues by unit IDs or employee list.
 * @property {Function} getIssuesByUnitIdsOrEmployeeListSuccess - Handles successful fetch of issues by unit IDs or employee list.
 * @property {Function} getIssuesByUnitIdsOrEmployeeMetricsSuccess - Handles successful fetch of issues with metrics.
 * @property {Function} getIssuesByUnitIdsOrEmployeeListFailure - Handles failed fetch of issues by unit IDs or employee list.
 * @property {Function} getIssueReportByFacilityId - Initiates fetching issue report by facility ID.
 * @property {Function} getIssueReportByFacilityIdSuccess - Handles successful fetch of issue report by facility ID.
 * @property {Function} getIssueReportByFacilityIdFailure - Handles failed fetch of issue report by facility ID.
 * @property {Function} setCurrentIssuePage - Sets the current page for issue pagination.
 * @property {Function} getFeedbackByUnitIds - Initiates fetching feedback by unit IDs.
 * @property {Function} getFeedbackByUnitIdsSuccess - Handles successful fetch of feedback by unit IDs.
 * @property {Function} getFeedbackByUnitIdsRecognitionMetricsSuccess - Handles successful fetch of recognition feedback with metrics.
 * @property {Function} getFeedbackByUnitIdsCoachingMetricsSuccess - Handles successful fetch of coaching feedback with metrics.
 * @property {Function} getFeedbackByUnitIdsFailure - Handles failed fetch of feedback by unit IDs.
 * @property {Function} getFeedbackByEmployeeList - Initiates fetching feedback by employee list.
 * @property {Function} getFeedbackByEmployeeListSuccess - Handles successful fetch of feedback by employee list.
 * @property {Function} getFeedbackByEmployeeListFailure - Handles failed fetch of feedback by employee list.
 * @property {Function} getFeedbackReportByFacilityId - Initiates fetching feedback report by facility ID.
 * @property {Function} getFeedbackReportByFacilityIdSuccess - Handles successful fetch of feedback report by facility ID.
 * @property {Function} getFeedbackReportByFacilityIdFailure - Handles failed fetch of feedback report by facility ID.
 * @property {Function} setCurrentFeedbackPage - Sets the current page for feedback pagination.
 * @property {Function} setTaskSearchQuery - Sets the search query for tasks and resets pagination.
 * @property {Function} setTaskSort - Sets the sort key and direction for tasks.
 * @property {Function} addTaskToSharingQ - Adds a task to the sharing queue.
 * @property {Function} clearSharingQ - Clears the task sharing queue.
 * @property {Function} shareAllTasks - Initiates sharing all tasks and sets loading state.
 * @property {Function} shareAllTasksSuccess - Handles successful sharing of all tasks.
 * @property {Function} shareAllTasksFailure - Handles failed sharing of all tasks.
 * @property {Function} setIssueFilter - Sets the filter for issues and resets pagination.
 * @property {Function} clearIssueFilter - Clears the issue filter and resets pagination.
 * @property {Function} setFeedbackFilter - Sets the filter for feedback and resets pagination.
 * @property {Function} clearFeedbackFilter - Clears the feedback filter and resets pagination.
 * @property {Function} selectDataForEdit - Selects data for editing in a modal.
 * @property {Function} resetSelectedDataForEdit - Clears selected data for editing.
 * @property {Function} getTasksByAccountNum - Initiates fetching tasks by account number.
 * @property {Function} getTasksByAccountNumSuccess - Handles successful fetch of tasks by account number.
 * @property {Function} getTasksByAccountNumFailure - Handles failed fetch of tasks by account number.
 * @property {Function} clearTasksByAccountNum - Clears tasks by account number from the state.
 * @property {Function} getTasksByEmployeeId - Initiates fetching tasks by employee ID.
 * @property {Function} getTasksByEmployeeIdSuccess - Handles successful fetch of tasks by employee ID.
 * @property {Function} getTasksByEmployeeIdFailure - Handles failed fetch of tasks by employee ID.
 * @property {Function} clearTasksByEmployeeId - Clears tasks by employee ID from the state.
 * @property {Function} saveTaskData - Saves temporary task data to the store.
 * @property {Function} clearTaskData - Clears temporary task data from the store.
 * @property {Function} saveTaskModalState - Saves modal state to the store.
 * @property {Function} clearTaskModalState - Clears modal state from the store.
 *
 * @example
 * dispatch(postNewTask(taskData));
 * dispatch(completeTask({ taskId: 'task-uuid' }));
 * dispatch(setIssueFilter({ status: ['open'] }));
 * dispatch(shareAllTasks({ userName: 'abc1234',email: ['jane@example.com'] }));
 *
 * @returns {object} The task slice reducer and actions.
 */
const taskSlice = createSlice({
  name: 'task',
  initialState: TaskContext,
  reducers: {
    postNewTask: (state, action: PayloadAction<TaskPost>) => {
      return {
        ...state,
        loadingPostNewTask: true
      };
    },
    postNewTaskSuccess: state => {
      return {
        ...state,
        loadingPostNewTask: false
      };
    },
    postNewTaskFailure: state => {
      return {
        ...state,
        loadingPostNewTask: false
      };
    },
    completeTask: (state, action: PayloadAction<ICompleteTaskPayload>) => {
      return {
        ...state,
        loadingCompleteTask: true
      };
    },
    completeTaskSuccess: state => {
      return {
        ...state,
        loadingCompleteTask: false
      };
    },
    completeTaskFailure: state => {
      return {
        ...state,
        loadingCompleteTask: false
      };
    },
    deleteTask: (state, action: PayloadAction<Feedback | Issue | TaskPost>) => {
      return {
        ...state,
        loadingDeleteTask: true
      };
    },
    deleteTaskSuccess: state => {
      return {
        ...state,
        loadingDeleteTask: false
      };
    },
    deleteTaskFailure: state => {
      return {
        ...state,
        loadingDeleteTask: false
      };
    },
    getCategoriesByType: (
      state,
      action: PayloadAction<IGetCategoriesByTypePayload>
    ) => {
      return {
        ...state
      };
    },
    getCategoriesByTypeSuccess: (state, action: PayloadAction<Category[]>) => {
      return {
        ...state,
        categories: action.payload
      };
    },
    getCategoriesByTypeFailure: state => {
      return {
        ...state,
        categories: []
      };
    },
    // ServiceCentral ticket details
    getDetailsByTicketNum: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        isLoadingTicket: true
      };
    },
    getDetailsByTicketNumSuccess: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        isLoadingTicket: false,
        ticketDetails: action.payload
      };
    },
    getDetailsByTicketNumFailure: state => {
      return {
        ...state,
        isLoadingTicket: false
      };
    },
    clearTicketDetails: state => {
      return { ...state, ticketDetails: '' };
    },

    getSCLocations: state => {
      return {
        ...state,
        isLoadingSCLocations: true
      };
    },
    getSCLocationsSuccess: (state, action: PayloadAction<SCLocation[]>) => {
      return {
        ...state,
        isLoadingSCLocations: false,
        scLocations: action.payload
      };
    },
    getSCLocationsFailure: state => {
      return {
        ...state,
        isLoadingSCLocations: false
      };
    },
    getGELocations: state => {
      return {
        ...state,
        isLoadingGELocations: true
      };
    },
    getGELocationsSuccess: (state, action: PayloadAction<GELocation[]>) => {
      return {
        ...state,
        isLoadingGELocations: false,
        geLocations: action.payload
      };
    },
    getGELocationsFailure: state => {
      return {
        ...state,
        isLoadingGELocations: false
      };
    },
    verifyControlNumber: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        isLoadingControlNumber: true
      };
    },
    verifyControlNumberSuccess: (
      state,
      action: PayloadAction<string | null>
    ) => {
      return {
        ...state,
        controlNumberResponse: action.payload,
        isLoadingControlNumber: false
      };
    },
    verifyControlNumberFailure: state => {
      return {
        ...state,
        controlNumberResponse: null,
        isLoadingControlNumber: false
      };
    },
    clearControlNumberResponse: state => {
      return {
        ...state,
        controlNumberResponse: null
      };
    },
    getTaskDiscussion: (state, action: PayloadAction<string>) => {
      return { ...state };
    },

    postTaskComment: (state, action: PayloadAction<IPostCommentPayload>) => {
      return { ...state };
    },

    searchIssues: (state, action: PayloadAction<IGetSearchIssuesPayload>) => {
      return {
        ...state
      };
    },
    searchIssuesSuccess: (state, action: PayloadAction<Issue[]>) => {
      return {
        ...state,
        searchedIssues: action.payload
      };
    },
    searchIssuesFailure: state => {
      return {
        ...state,
        searchedIssues: []
      };
    },
    clearSearchIssues: state => {
      return {
        ...state,
        searchedIssues: []
      };
    },
    getIssuesByUnitIdsOrEmployeeList: (
      state,
      action: PayloadAction<GetTaskParams>
    ) => {
      return {
        ...state,
        // TODO: Is this right
        loadingIssuesByUnitIdsOrEmployeeList: true
      };
    },
    getIssuesByUnitIdsOrEmployeeListSuccess: (
      state,
      action: PayloadAction<{ issues: Issue[]; totalCount: number }>
    ) => {
      return {
        ...state,
        loadingIssuesByUnitIdsOrEmployeeList: false,
        issues: action.payload.issues,
        totalCount: action.payload.totalCount
      };
    },
    getIssuesByUnitIdsOrEmployeeMetricsSuccess: (
      state,
      action: PayloadAction<{
        totalCount: number;
        completedRecordsCount: number;
        openedRecordsCount: number;
      }>
    ) => {
      return {
        ...state,
        loadingIssuesByUnitIdsOrEmployeeList: false,
        loadingIssuesByFacility: false,
        issueMetrics: {
          totalRecordsCount: action.payload.totalCount,
          completedRecordsCount: action.payload.completedRecordsCount,
          openedRecordsCount: action.payload.openedRecordsCount
        }
      };
    },
    getIssuesByUnitIdsOrEmployeeListFailure: state => {
      return {
        ...state,
        loadingIssuesByUnitIdsOrEmployeeList: false,
        issues: [],
        totalCount: 0
      };
    },

    getIssuesByUnitIdsOrEmployeeListDownload: (
      state,
      action: PayloadAction<GetTaskParams>
    ) => {
      return {
        ...state
      };
    },
    // Get issues report by facility mnemonic
    getIssueReportByFacilityId: (
      state,
      action: PayloadAction<GetTaskParams>
    ) => {
      return {
        ...state,
        loadingIssuesByFacility: true
      };
    },
    // Get issues report by facility mnemonic
    getIssueReportByFacilityIdSuccess: (
      state,
      action: PayloadAction<{ issues: Issue[]; totalCount: number }>
    ) => {
      return {
        ...state,
        loadingIssuesByFacility: false,
        issues: action.payload.issues,
        totalCount: action.payload.totalCount
      };
    },
    // Get issues report by facility mnemonic
    getIssueReportByFacilityIdFailure: state => {
      return {
        ...state,
        loadingIssuesByFacility: false
      };
    },

    // Get all pages of issues report by facility mnemonic for printing csv
    getIssueReportByFacilityIdDownload: (
      state,
      action: PayloadAction<GetTaskParams>
    ) => {
      return { ...state };
    },

    setCurrentIssuePage: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        currentIssuePage: action.payload
      };
    },
    // TODO: Needs larger query type
    getFeedbackByUnitIds: (state, action: PayloadAction<any>) => {
      return {
        ...state,
        loadingFeedbackByUnitIds: true
      };
    },
    getFeedbackByUnitIdsSuccess: (
      state,
      action: PayloadAction<{ feedbackList: Feedback[]; totalCount: number }>
    ) => {
      return {
        ...state,
        loadingFeedbackByUnitIds: false,
        feedback: action.payload.feedbackList,
        totalCount: action.payload.totalCount
      };
    },
    getFeedbackByUnitIdsRecognitionMetricsSuccess: (
      state,
      action: PayloadAction<{
        totalCount: number;
        completedRecordsCount: number;
        openedRecordsCount: number;
      }>
    ) => {
      return {
        ...state,
        loadingFeedbackByUnitIds: false,
        loadingFeedbackByFacility: false,
        recognitionMetrics: {
          totalRecordsCount: action.payload.totalCount,
          completedRecordsCount: action.payload.completedRecordsCount,
          openedRecordsCount: action.payload.openedRecordsCount
        }
      };
    },
    getFeedbackByUnitIdsCoachingMetricsSuccess: (
      state,
      action: PayloadAction<{
        totalCount: number;
        completedRecordsCount: number;
        openedRecordsCount: number;
      }>
    ) => {
      return {
        ...state,
        loadingFeedbackByUnitIds: false,
        loadingFeedbackByFacility: false,
        coachingMetrics: {
          totalRecordsCount: action.payload.totalCount,
          completedRecordsCount: action.payload.completedRecordsCount,
          openedRecordsCount: action.payload.openedRecordsCount
        }
      };
    },
    getFeedbackByUnitIdsFailure: state => {
      return {
        ...state,
        loadingFeedbackByUnitIds: false,
        feedback: [],
        totalCount: 0
      };
    },
    getFeedbackByEmployeeList: (state, action: PayloadAction<any>) => {
      return {
        ...state,
        loadingFeedbackByEmployeeList: true
      };
    },
    getFeedbackByEmployeeListSuccess: (
      state,
      action: PayloadAction<{ feedbackList: Feedback[]; totalCount: number }>
    ) => {
      return {
        ...state,
        loadingFeedbackByEmployeeList: false,
        feedback: action.payload.feedbackList,
        totalCount: action.payload.totalCount
      };
    },
    getFeedbackByEmployeeListFailure: state => {
      return {
        ...state,
        loadingFeedbackByEmployeeList: false,
        feedback: [],
        totalCount: 0
      };
    },
    getFeedbackByUnitIdsOrEmployeeListDownload: (
      state,
      action: PayloadAction<any>
    ) => {
      return {
        ...state
      };
    },
    // Get feedback report by facility mnemonic
    getFeedbackReportByFacilityId: (
      state,
      action: PayloadAction<GetTaskParams>
    ) => {
      return {
        ...state,
        loadingFeedbackByFacility: true
      };
    },
    getFeedbackReportByFacilityIdSuccess: (
      state,
      action: PayloadAction<{ feedbackList: Feedback[]; totalCount: number }>
    ) => {
      return {
        ...state,
        loadingFeedbackByFacility: false,
        feedback: action.payload.feedbackList,
        totalCount: action.payload.totalCount
      };
    },
    getFeedbackReportByFacilityIdFailure: state => {
      return {
        ...state,
        loadingFeedbackByFacility: false,
        feedback: [],
        totalCount: 0
      };
    },
    // Get all pages of feedback report by facility mnemonic for printing csv
    getFeedbackReportByFacilityIdDownload: (
      state,
      action: PayloadAction<GetTaskParams>
    ) => {
      return { ...state };
    },
    setCurrentFeedbackPage: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        currentFeedbackPage: action.payload
      };
    },
    setTaskSearchQuery: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        taskSearchQuery: action.payload,
        currentIssuePage: 1,
        currentFeedbackPage: 1
      };
    },
    setTaskSort: (
      state,
      action: PayloadAction<{ sortKey: string; sortDir: string }>
    ) => {
      return {
        ...state,
        ...action.payload
      };
    },
    addTaskToSharingQ: (state, action: PayloadAction<ITaskShareObj>) => {
      return {
        ...state,
        sharingQ: [...state.sharingQ, action.payload]
      };
    },
    clearSharingQ: state => {
      return {
        ...state,
        sharingQ: []
      };
    },
    shareAllTasks: (state, action: PayloadAction<ITaskShareObj>) => {
      return {
        ...state,
        sharing: true
      };
    },
    shareAllTasksSuccess: state => {
      return {
        ...state,
        sharing: false
      };
    },
    shareAllTasksFailure: state => {
      return {
        ...state,
        sharing: false
      };
    },
    setIssueFilter: (state, action: PayloadAction<Filter>) => {
      return {
        ...state,
        issueFilter: action.payload,
        currentIssuePage: 1
      };
    },
    clearIssueFilter: state => {
      return {
        ...state,
        issueFilter: {} as Filter,
        currentIssuePage: 1
      };
    },
    setFeedbackFilter: (state, action: PayloadAction<Filter>) => {
      return {
        ...state,
        feedbackFilter: action.payload,
        currentFeedbackPage: 1
      };
    },
    clearFeedbackFilter: state => {
      return {
        ...state,
        feedbackFilter: {} as Filter,
        currentFeedbackPage: 1
      };
    },
    // TODO: Are these two actions really needed or can modal data be passed directly?
    selectDataForEdit: (state, action: PayloadAction<Feedback>) => {
      return {
        ...state,
        selectedData: action.payload
      };
    },
    resetSelectedDataForEdit: state => {
      return {
        ...state,
        selectedData: null
      };
    },
    // TODO: Needs loading state
    getTasksByAccountNum: (
      state,
      action: PayloadAction<IGetTasksByAccountNumPayload>
    ) => {
      return {
        ...state
      };
    },
    getTasksByAccountNumSuccess: (
      state,
      action: PayloadAction<(Issue | Feedback)[]>
    ) => {
      return {
        ...state,
        patientTasks: action.payload
      };
    },
    getTasksByAccountNumFailure: state => {
      return {
        ...state
      };
    },
    clearTasksByAccountNum: state => {
      return {
        ...state,
        patientTasks: []
      };
    },
    // TODO: Needs loading state
    getTasksByEmployeeId: (
      state,
      action: PayloadAction<IGetTasksByEmployeeIdPayload>
    ) => {
      return {
        ...state
      };
    },
    getTasksByEmployeeIdSuccess: (
      state,
      action: PayloadAction<{ issues: Issue[]; feedback: Feedback[] }>
    ) => {
      return {
        ...state,
        employeeIssues: action.payload.issues,
        employeeFeedback: action.payload.feedback
      };
    },
    getTasksByEmployeeIdFailure: state => {
      return {
        ...state,
        employeeIssues: [],
        employeeFeedback: []
      };
    },
    clearTasksByEmployeeId: state => {
      return {
        ...state,
        employeeIssues: [],
        employeeFeedback: []
      };
    },
    // Save task data to store
    saveTaskData: (state, action: PayloadAction<ITempTaskWithSharedWith>) => {
      return {
        ...state,
        tempTaskObject: action.payload.taskObject,
        tempShareWithEmailObject: action.payload.shareObject,
        tempShareWithDisplay: action.payload.sharedWithDisplay
      };
    },
    // Clear task data in store
    clearTaskData: state => {
      return {
        ...state,
        tempTaskObject: {} as ITempTaskWithNewStoplight,
        tempShareWithEmailObject: {} as ITaskShareObj,
        tempShareWithDisplay: []
      };
    },
    // TODO: Is this necessary?
    // Save modal state to store
    saveTaskModalState: (
      state,
      action: PayloadAction<ISaveTaskModalStatePayload>
    ) => {
      return {
        ...state,
        tempModalState: action.payload
      };
    },
    // Clear modal state in store
    clearTaskModalState: state => {
      return {
        ...state,
        tempModalState: {} as ISaveTaskModalStatePayload,
        redirect: false
      };
    }
  }
});

export const {
  // Actions
  postNewTask,
  postNewTaskSuccess,
  postNewTaskFailure,
  completeTask,
  completeTaskSuccess,
  completeTaskFailure,
  deleteTask,
  deleteTaskSuccess,
  deleteTaskFailure,
  getCategoriesByType,
  getCategoriesByTypeSuccess,
  getCategoriesByTypeFailure,
  getDetailsByTicketNum,
  getDetailsByTicketNumSuccess,
  getDetailsByTicketNumFailure,
  clearTicketDetails,
  getSCLocations,
  getSCLocationsSuccess,
  getSCLocationsFailure,
  getGELocations,
  getGELocationsSuccess,
  getGELocationsFailure,
  verifyControlNumber,
  verifyControlNumberSuccess,
  verifyControlNumberFailure,
  clearControlNumberResponse,
  getTaskDiscussion,
  postTaskComment,
  searchIssues,
  searchIssuesSuccess,
  searchIssuesFailure,
  clearSearchIssues,
  getIssuesByUnitIdsOrEmployeeList,
  getIssuesByUnitIdsOrEmployeeListSuccess,
  getIssuesByUnitIdsOrEmployeeMetricsSuccess,
  getIssuesByUnitIdsOrEmployeeListFailure,
  getIssuesByUnitIdsOrEmployeeListDownload,
  getIssueReportByFacilityId,
  getIssueReportByFacilityIdSuccess,
  getIssueReportByFacilityIdFailure,
  getIssueReportByFacilityIdDownload,
  setCurrentIssuePage,
  getFeedbackByUnitIds,
  getFeedbackByUnitIdsSuccess,
  getFeedbackByUnitIdsRecognitionMetricsSuccess,
  getFeedbackByUnitIdsCoachingMetricsSuccess,
  getFeedbackByUnitIdsFailure,
  getFeedbackByEmployeeList,
  getFeedbackByEmployeeListSuccess,
  getFeedbackByEmployeeListFailure,
  getFeedbackByUnitIdsOrEmployeeListDownload,
  getFeedbackReportByFacilityId,
  getFeedbackReportByFacilityIdSuccess,
  getFeedbackReportByFacilityIdFailure,
  getFeedbackReportByFacilityIdDownload,
  setCurrentFeedbackPage,
  setTaskSearchQuery,
  setTaskSort,
  addTaskToSharingQ,
  clearSharingQ,
  shareAllTasks,
  shareAllTasksSuccess,
  shareAllTasksFailure,
  setIssueFilter,
  clearIssueFilter,
  setFeedbackFilter,
  clearFeedbackFilter,
  selectDataForEdit,
  resetSelectedDataForEdit,
  getTasksByAccountNum,
  getTasksByAccountNumSuccess,
  getTasksByAccountNumFailure,
  clearTasksByAccountNum,
  getTasksByEmployeeId,
  getTasksByEmployeeIdSuccess,
  getTasksByEmployeeIdFailure,
  clearTasksByEmployeeId,
  saveTaskData,
  clearTaskData,
  saveTaskModalState,
  clearTaskModalState
} = taskSlice.actions;

export default taskSlice.reducer;
